// src/pages/LayoutsPage.tsx
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import { Add, BackupTable, Edit, Delete, ReadMore, Upload } from '@mui/icons-material';
import SearchBar from '@/components/form/SearchBar';
import LayoutInfoCard from '@/components/layout/LayoutInfoCard';
import LayoutSubheading from '@/components/layout/LayoutSubheading';
import BaseButton from '@/components/ui/BaseButton';
import BaseCard from '@/components/ui/BaseCard';
import BasePaper from '@/components/ui/BasePaper';
import BaseTitle from '@/components/ui/BaseTitle';
import CardIconButton from '@/components/layout/CardIconButton';
import '@/styles/pages/Layouts/LayoutsPage.scss';
import BulkAssignDialog from '@/components/layout/BulkAssignDialog';
import { fetchLayoutPackagesByCorporateId, importLayoutPackageAPI } from '@/api/layout';
import { LayoutPackage } from '@/types/pages/layout';
import localforage from 'localforage';
import noPreviewImage from '@/assets/images/no-preview.png';
import BaseDialog from '@/components/ui/BaseDialog';
import { deleteLayoutPackageAPI } from '@/api/layout';
import { useAppContext } from '@/provider/contexts';
import { hideLoading, showLoading } from '@/components/ui/LoadingOverlay';
import { useTranslation } from 'react-i18next';

export default function LayoutsPage() {
    const { t } = useTranslation('common');
    const { setNotification } = useAppContext();
    const [bust] = useState(() => Date.now());
    const getLayoutImages = (images?: string[]) => {
        const base = images && images.length > 0 ? images : [noPreviewImage];
        return base.map((url) => `${url}?t=${bust}`);
    };
    const navigate = useNavigate();
    const [isBulkAssignDialogOpen, setIsBulkAssignDialogOpen] = useState(false);
    const [layoutPackages, setLayoutPackages] = useState<LayoutPackage[]>([]);
    const [searchText, setSearchText] = useState('');
    const [selectedPackageToDelete, setSelectedPackageToDelete] = useState<LayoutPackage | null>(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [, setDisplayList] = useState<LayoutPackage[]>(layoutPackages);
    const [selectedPackageForBulk, setSelectedPackageForBulk] = useState<LayoutPackage | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const corporationId = await localforage.getItem<number>('corporation_id');
            if (!corporationId) {
                console.warn('No corporation_id found in local storage.');
                return;
            }

            const res = await fetchLayoutPackagesByCorporateId(corporationId);
            const body = res.data;
            if (body.data?.layout_packages) {
                const packages = body.data.layout_packages as any[];

                const layoutsWithImages: any[] = packages.map((pkg) => {
                    const sliderImgs: string[] =
                        Array.isArray(pkg.latest_version?.slider_images) && pkg.latest_version.slider_images.length > 0
                            ? pkg.latest_version.slider_images
                            : [];

                    return {
                        ...pkg,
                        images: sliderImgs.length > 0 ? sliderImgs : [noPreviewImage],
                    };
                });

                setLayoutPackages(layoutsWithImages);
            } else {
                console.warn('Unexpected API structure:', res);
                setLayoutPackages([]);
            }
        } catch (error) {
            console.error('Failed to fetch layout packages:', error);
            setLayoutPackages([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = () => {
        setDisplayList(layoutPackages.filter((pkg) => pkg.name.toLowerCase().includes(searchText.toLowerCase())));
    };

    const [overwriteToken, setOverwriteToken] = useState<string | null>(null);
    const [isOverwriteDialogOpen, setIsOverwriteDialogOpen] = useState(false);
    const lastSelectedFile = useRef<File | null>(null);

    const handleFileChange = async (_event: React.ChangeEvent<HTMLInputElement>) => {
        const file = _event.target.files?.[0] ?? null;
        _event.target.value = '';
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.zip')) {
            setNotification({
                message: t('Uploaded file is not a valid AURA Layout Package file.'),
                severity: 'error',
            });
            return;
        }
        showLoading();
        lastSelectedFile.current = file;
        try {
            const resp = await importLayoutPackageAPI(file);
            const body = resp.data;
            const token = body.data?.layout_token;

            if (token && body.status === 400) {
                hideLoading();
                setOverwriteToken(token);
                setIsOverwriteDialogOpen(true);
            } else {
                setNotification({ message: t('Import succeeded!'), severity: 'success' });
                fetchData();
            }
        } catch (err: any) {
            console.error(err);
            setNotification({ message: t('Import failed.'), severity: 'error' });
        } finally {
            hideLoading();
        }
    };

    const handleConfirmOverwrite = async () => {
        setIsOverwriteDialogOpen(false);
        if (!overwriteToken) return;
        setIsLoading(true);
        try {
            await importLayoutPackageAPI({ layout_token: overwriteToken });
            setNotification({ message: t('Import succeeded!'), severity: 'success' });
            fetchData();
        } catch {
            setNotification({ message: t('Import failed.'), severity: 'error' });
        } finally {
            setIsLoading(false);
            setOverwriteToken(null);
            lastSelectedFile.current = null;
        }
    };

    const handleConfirmDelete = async () => {
        if (!selectedPackageToDelete) return;

        try {
            const corporationId = await localforage.getItem<number>('corporation_id');
            if (!corporationId) throw new Error('Missing corporation_id');

            // always delete all versions
            const versionParam = 'all' as const;

            await deleteLayoutPackageAPI(corporationId, selectedPackageToDelete.id, versionParam);

            setLayoutPackages((prev) => prev.filter((pkg) => pkg.id !== selectedPackageToDelete.id));
        } catch (err) {
            console.error('Failed to delete layout package', err);
        } finally {
            setIsDeleteDialogOpen(false);
            setSelectedPackageToDelete(null);
        }
    };
    return (
        <>
            <BasePaper>
                <BaseTitle
                    icon={
                        <Box sx={{ pt: '3px' }}>
                            <BackupTable />
                        </Box>
                    }
                    title={t('Corporation Layouts')}
                />
                <BaseCard
                    sx={{
                        pt: 2,
                        px: { xs: 1.5, md: 2, lg: 6 },
                        height: '800px',
                        overflowY: 'auto',
                    }}
                >
                    <LayoutSubheading>{t('Available Layout Packages:')}</LayoutSubheading>
                    <Box
                        className="card-header"
                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                        <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                            <BaseButton startIcon={<Add />} onClick={() => navigate('/admin/layouts/new')}>
                                {t('New Layout Package')}
                            </BaseButton>

                            <input
                                data-testid="layout-import-input"
                                type="file"
                                ref={fileInputRef}
                                accept=".zip"
                                hidden
                                onChange={handleFileChange}
                            />
                            <BaseButton startIcon={<Upload />} onClick={() => fileInputRef.current?.click()}>
                                {t('Import')}
                            </BaseButton>
                        </Box>

                        <SearchBar
                            value={searchText}
                            sx={{ my: 2 }}
                            onChange={(e) => setSearchText(e.target.value)}
                            onSearch={handleSearch}
                        />
                    </Box>

                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box className="layout-cards-container" sx={{ mb: 4 }}>
                            {layoutPackages
                                .filter((pkg) => pkg.name.toLowerCase().includes(searchText.toLowerCase()))
                                .map((layoutPackage) => (
                                    <LayoutInfoCard
                                        key={layoutPackage.id}
                                        id={layoutPackage.id.toString()}
                                        images={getLayoutImages(layoutPackage.images)}
                                        name={layoutPackage.name}
                                        description={''}
                                    >
                                        <LayoutInfoCard.Chips>
                                            <Chip
                                                size="small"
                                                variant="filled"
                                                color="primary"
                                                label={`V ${layoutPackage.latest_version?.version_number ?? '-'}`}
                                            />
                                        </LayoutInfoCard.Chips>
                                        <LayoutInfoCard.Actions>
                                            <Box>
                                                <CardIconButton
                                                    color="primary"
                                                    sx={{ mr: 1 }}
                                                    onClick={() => navigate(`/admin/layouts/${layoutPackage.id}`)}
                                                >
                                                    <Edit />
                                                </CardIconButton>
                                                <CardIconButton
                                                    color="error"
                                                    onClick={() => {
                                                        setSelectedPackageToDelete(layoutPackage);
                                                        setIsDeleteDialogOpen(true);
                                                    }}
                                                >
                                                    <Delete />
                                                </CardIconButton>
                                            </Box>
                                            <BaseButton
                                                size="small"
                                                startIcon={<ReadMore />}
                                                onClick={() => {
                                                    setSelectedPackageForBulk(layoutPackage);
                                                    setIsBulkAssignDialogOpen(true);
                                                }}
                                            >
                                                {t('Bulk Assign')}
                                            </BaseButton>
                                        </LayoutInfoCard.Actions>
                                    </LayoutInfoCard>
                                ))}
                        </Box>
                    )}
                </BaseCard>
            </BasePaper>

            <BulkAssignDialog
                isOpen={isBulkAssignDialogOpen}
                layoutPackageId={selectedPackageForBulk?.id ?? 0}
                versionNumber={selectedPackageForBulk?.latest_version?.version_number}
                onConfirm={() => {
                    setIsBulkAssignDialogOpen(false);
                    setSelectedPackageForBulk(null);
                }}
                onClose={() => {
                    setIsBulkAssignDialogOpen(false);
                    setSelectedPackageForBulk(null);
                }}
            />

            <BaseDialog
                open={isDeleteDialogOpen}
                dialogTitle={t('Delete Layout Package')}
                confirmButtonText={t('Delete')}
                cancelButtonText={t('Cancel')}
                onConfirm={handleConfirmDelete}
                onClose={() => {
                    setIsDeleteDialogOpen(false);
                    setSelectedPackageToDelete(null);
                }}
            >
                {t('Are you sure you want to delete layout package “{{name}}”?', {
                    name: selectedPackageToDelete?.name ?? '',
                })}
            </BaseDialog>
            <BaseDialog
                open={isOverwriteDialogOpen}
                dialogTitle={t('Confirm Overwrite')}
                confirmButtonText={t('Overwrite')}
                cancelButtonText={t('Cancel')}
                onConfirm={handleConfirmOverwrite}
                onClose={() => setIsOverwriteDialogOpen(false)}
            >
                <Typography>
                    {t('This package name is already in use. Confirm overwrite?')}
                    <br />
                    <em>{t('Note: existing store layouts will not be affected.')}</em>
                </Typography>
            </BaseDialog>
        </>
    );
}
